/**
 * usePageTurn — swipe, tap zones, arrow keys, and the follow model.
 *
 * ⚠️ **LAW 12. Turning a page is browsing. It never moves the narration.**
 *
 * design.md §5.5: "This is the most important interaction decision in the reader and no subsystem
 * stated it: conflating them means you cannot flip back to check a name without the voice jumping,
 * which is the single most common thing a listener does."
 *
 * The seam was built in P1 — `following`, `narrationColumn`, `backToNarration()` and the pill's
 * place in the layout — before there was any audio to move, because the alternative is P2 discovering
 * that "turn the page" and "seek the audio" were the same function all along and refactoring the
 * reader to separate them. P2 supplies the one thing that was missing: a `narrationColumn` that is
 * not null.
 *
 * The seam, stated as an invariant a reviewer can check: **`turn()` writes to the Paginator and to
 * nothing else.** If a future edit gives this file a reference to an AudioEngine, LAW 12 is gone.
 *
 * ── P2 kept the invariant, and changed four things ──────────────────────────
 * `narrationColumn` is no longer always null — `useCueSync` computes it from `columnOf(sid)` — and
 * `turn()` still writes to the Paginator and to nothing else. What P2 added:
 *
 *   1. `onTogglePlay`, which is the resolution of the **Space-key conflict this file named in P1**
 *      and deliberately left open. It is a callback, not an engine: this hook still cannot seek,
 *      cannot pause a specific element, and cannot address audio at all.
 *   2. `showFollowPill` now also requires that the voice is on a *different* page-turn. The pill
 *      offering to take you where you already are is a control with no effect, sitting over the text
 *      for twenty seconds.
 *   3. Auto-refollow compares **turns**, not columns — in spread mode the voice is regularly on the
 *      right-hand column of the spread you are looking at, and a column comparison decides you are
 *      somewhere else and never refollows.
 *   4. `narrationTurn`, so the pill can say *"Karti is on page 14"* using the same arithmetic the
 *      footer uses for "page 7 of 21". Two widgets, one number.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { Paginator } from "./Paginator.ts";

export type TurnSource =
  /** A finger. */
  | "swipe"
  /** A tap in a page zone. */
  | "tap"
  /** A key. */
  | "key"
  /** The chapter drawer, a deep link, a restored position. */
  | "nav"
  /** P2 only: the scheduled turn when a cue crosses a page boundary. The ONLY non-browsing turn. */
  | "narration";

export interface PageTurnOptions {
  frame: HTMLElement | null;
  paginator: Paginator | null;
  enabled: boolean;
  /** A turn that would leave this chapter part. `dir` is -1 (backwards) or +1 (forwards). */
  onOverflow(dir: -1 | 1, source: TurnSource): void;
  /** A turn landed. `column` is the Paginator's column index (the left one in spread mode). */
  onTurned(column: number, source: TurnSource, dir: -1 | 0 | 1): void;
  /** A tap in the centre zone. */
  onToggleChrome(): void;
  /**
   * P2 seam. The column the narration is currently reading, or null when nothing is playing.
   * P1 always passes null; P2 passes `useCueSync`'s `narrationColumn`.
   */
  narrationColumn?: number | null;
  /**
   * P2. Play/pause, and **the only reason this hook knows a narration exists**.
   *
   * P1 recorded the conflict in the Space-key case below and refused to guess: "a media player uses
   * Space for play/pause, and this app is both. When narration lands, pick one deliberately." This
   * is the pick. When a narration is present Space is play/pause and Shift+Space turns back; when it
   * is absent (a book with no audio, an anonymous reader on an un-narrated page) Space keeps P1's
   * behaviour, because a reader with no voice whose Space bar does nothing is just broken.
   *
   * It is a bare callback and not an engine handle on purpose. A reference to an `AudioEngine` in
   * this file is how LAW 12 dies — not by anyone deciding to break it, but by the next person
   * finding a seek already in scope.
   */
  onTogglePlay?: (() => void) | null;
}

export interface PageTurnApi {
  /** LAW 12's flag. True when the page follows the voice; false after any manual turn. */
  following: boolean;
  /** True when the follow pill should be shown: the voice is on a page the reader is not on. */
  showFollowPill: boolean;
  /** 1-based page-turn number the voice is on, for the pill's copy. Null when nothing is playing. */
  narrationTurn: number | null;
  /** Turn by `delta` pages. Any source but `"narration"` clears `following`. */
  turn(delta: number, source: TurnSource): void;
  /** Go to an absolute column. Same follow semantics as `turn`. */
  goTo(column: number, source: TurnSource, opts?: { animate?: boolean }): void;
  /**
   * "I have deliberately gone somewhere else", for a navigation that is not a turn — the chapter
   * drawer. Without it the voice's page drags the reader straight back. See the implementation.
   */
  stopFollowing(): void;
  /** Set the follow flag from room chrome without moving either the reader or the room. */
  setFollowing(on: boolean): void;
  /** The pill's action: turn back to the voice. **Turns, does not seek.** */
  backToNarration(): void;
}

/** §5.5: "Auto-refollow ... after 20 s idle with that page displayed. Never by moving the user." */
const REFOLLOW_IDLE_MS = 20_000;

/** A press held this long with no movement is a selection or (P3) a long-press menu, not a swipe. */
const LONG_PRESS_MS = 450;

/** Movement under this is a tap, not a drag. 10 px is §5.6's own slop for the long-press. */
const TAP_SLOP_PX = 10;

/** A tap must also be quick; a slow press that never moved is a long-press, handled above. */
const TAP_MAX_MS = 450;

/** Committed-drag threshold: a fifth of a page, capped, or a fast enough flick. */
const FLICK_PX_PER_MS = 0.45;

export function usePageTurn(opts: PageTurnOptions): PageTurnApi {
  const { frame, paginator, enabled, narrationColumn = null } = opts;

  const [following, setFollowing] = useState(true);

  // Callbacks change identity every render; the pointer handlers are attached once. A ref keeps
  // the handlers stable without making them stale.
  const cb = useRef(opts);
  cb.current = opts;

  const turn = useCallback(
    (delta: number, source: TurnSource) => {
      const p = cb.current.paginator;
      if (!p || delta === 0) return;
      if (source !== "narration") setFollowing(false);

      const dir: -1 | 1 = delta < 0 ? -1 : 1;
      if ((dir < 0 && p.atStart()) || (dir > 0 && p.atEnd())) {
        // The part is exhausted. Only the reader knows whether there is another part, another
        // chapter, or the end of the book — this hook deliberately knows nothing about the
        // document.
        cb.current.onOverflow(dir, source);
        return;
      }
      const col = p.turn(delta);
      cb.current.onTurned(col, source, dir);
    },
    [],
  );

  const goTo = useCallback((column: number, source: TurnSource, o?: { animate?: boolean }) => {
    const p = cb.current.paginator;
    if (!p) return;
    if (source !== "narration") setFollowing(false);
    const before = p.page;
    const col = p.goTo(column, o);
    cb.current.onTurned(col, source, col === before ? 0 : col > before ? 1 : -1);
  }, []);

  /**
   * "I have deliberately gone somewhere else" — for navigations that are not turns.
   *
   * ⚠️ Every *turn* already clears `following` inside `turn()`/`goTo()`, but a **chapter chosen from
   * the drawer** goes nowhere near this hook: it re-mounts a different chapter through `goToChapter`,
   * and `following` stayed true. So the reader arrived at chapter VIII, `useNarration` opened the
   * page the room/engine was still on, `followToSid` saw a new first sid and — correctly, by its own
   * rule — took the reader back. Measured in a room, in real Chrome: `chapters/12` fetched,
   * `c012.p00/narration` fetched, then `c005.p00/narration` and `chapters/5`, and the anchor back at
   * `c005.b0000.s0` inside two seconds, with the drawer looking like a control that does nothing.
   *
   * It is not a room bug — a room only makes it certain, because a room opens a page for you the
   * moment you join, where the plain reader waits for somebody to press Play.
   */
  const stopFollowing = useCallback(() => setFollowing(false), []);

  const backToNarration = useCallback(() => {
    const p = cb.current.paginator;
    const target = cb.current.narrationColumn;
    if (!p || target == null) return;
    // Turns. Does **not** seek. This is the whole of LAW 12 expressed as one method: the pill's
    // promise is "take me back to where Karti is", not "restart the audio from here".
    const col = p.goTo(target);
    setFollowing(true);
    cb.current.onTurned(col, "narration", 0);
  }, []);

  /* ── Auto-refollow ────────────────────────────────────────────────────────
     §5.5: "Auto-refollow when the user lands back on the narration's page, or after 20 s idle with
     that page displayed. Never by moving the user." The second clause is the important one — this
     effect may set a flag, and may never call goTo(). */
  useEffect(() => {
    if (following || narrationColumn == null || !paginator) return;
    // ⚠️ **Turns, not columns.** In spread mode `paginator.page` is the LEFT column and is always
    // even, while the voice is on whichever column its sentence's first rect landed in — so half of
    // all "the reader is looking straight at the narrated sentence" moments have
    // `page !== narrationColumn` and would never refollow. On a MacBook, which is the machine
    // spread mode exists for, that is every other page.
    if (turnOf(paginator, paginator.page) !== turnOf(paginator, narrationColumn)) return;
    const t = setTimeout(() => setFollowing(true), REFOLLOW_IDLE_MS);
    return () => clearTimeout(t);
  }, [following, narrationColumn, paginator, paginator?.page]);

  /* ── Pointer: drag, flick, tap zones ──────────────────────────────────────── */
  useEffect(() => {
    if (!frame || !enabled) return;

    let pointerId: number | null = null;
    let startX = 0;
    let startY = 0;
    let startT = 0;
    let lastX = 0;
    let lastT = 0;
    let isDragging = false;
    /** The gesture started in an OS edge zone, or on something interactive: taps only, no drag. */
    let inert = false;
    let abandoned = false;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;

    const reset = () => {
      if (longPressTimer) clearTimeout(longPressTimer);
      longPressTimer = null;
      if (isDragging) {
        cb.current.paginator?.clearDragOffset();
        cb.current.paginator?.setDragging(false);
      }
      pointerId = null;
      isDragging = false;
      inert = false;
      abandoned = false;
    };

    const edgeDeadZone = (): number => {
      const raw = getComputedStyle(frame).getPropertyValue("--edge-dead-zone");
      const n = parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 24;
    };

    const onPointerDown = (e: PointerEvent) => {
      if (pointerId !== null) {
        // A second finger. That is a pinch — §5.2 pitfall (3) keeps pinch-zoom working on a
        // reading app deliberately — so we get out of its way entirely rather than fighting it.
        abandoned = true;
        reset();
        return;
      }
      if (e.button !== 0 && e.pointerType === "mouse") return;

      const rect = frame.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const dz = edgeDeadZone();

      pointerId = e.pointerId;
      startX = lastX = e.clientX;
      startY = e.clientY;
      startT = lastT = e.timeStamp;
      isDragging = false;
      abandoned = false;

      // §5.2 pitfall (3): "a 24 px dead zone on both edges left to the OS". iOS's edge swipe-back
      // begins there and it always wins; a drag started in that band produces a page that follows
      // the finger and then snaps back as the OS takes over, which reads as the app being broken.
      // Taps in the band are fine — the dead zone is about the *swipe*.
      const nearEdge = x <= dz || x >= rect.width - dz;
      const interactive = (e.target as Element | null)?.closest?.(
        "a, button, input, select, textarea, [role='button'], [data-no-turn]",
      );
      inert = nearEdge || Boolean(interactive);

      // A press held without moving is a text selection (P1) or the sentence menu (P3). Either
      // way it is not a page turn, and the gesture must release its claim on the pointer.
      longPressTimer = setTimeout(() => {
        if (!isDragging) abandoned = true;
      }, LONG_PRESS_MS);
    };

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerId !== pointerId || abandoned) return;
      const p = cb.current.paginator;
      if (!p) return;

      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      lastX = e.clientX;
      lastT = e.timeStamp;

      if (!isDragging) {
        if (inert) return;
        // A vertical intent is never a page turn. Bailing out (rather than just not starting)
        // matters because the browser may still be deciding whether this is a scroll or a
        // selection, and a half-committed drag that hands back control mid-gesture stutters.
        if (Math.abs(dy) > 14 && Math.abs(dy) > Math.abs(dx)) {
          abandoned = true;
          return;
        }
        if (Math.abs(dx) < TAP_SLOP_PX || Math.abs(dx) <= Math.abs(dy) * 1.2) return;

        isDragging = true;
        if (longPressTimer) clearTimeout(longPressTimer);
        longPressTimer = null;
        // Drag state lives on the DOM (`.pf-frame[data-dragging]`), never in React: a `useState`
        // here would re-render the whole reader on every gesture start and end, and nothing in the
        // tree needs to know.
        p.setDragging(true);
        try {
          frame.setPointerCapture(e.pointerId);
        } catch {
          /* Capture is an optimisation: without it a fast drag that leaves the frame is dropped
             at the boundary and snaps back. Not fatal. */
        }
      }

      p.setDragOffset(resist(dx, p));
    };

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerId !== pointerId) return;
      const p = cb.current.paginator;
      const dx = e.clientX - startX;
      const dt = Math.max(1, e.timeStamp - startT);
      const wasDragging = isDragging;
      const wasAbandoned = abandoned;
      const wasInert = inert;

      if (longPressTimer) clearTimeout(longPressTimer);
      longPressTimer = null;

      if (wasDragging && p) {
        // Commit or snap back. `--drag` is cleared and `--page` set in the same style flush with
        // the transition already restored, so the transform interpolates from wherever the finger
        // left it — no jump to the un-dragged position first.
        p.setDragging(false);
        p.clearDragOffset();

        if (!wasAbandoned) {
          const velocity = (e.clientX - lastX) / Math.max(1, e.timeStamp - lastT);
          const threshold = Math.min(72, (p.geometry?.pageW ?? 360) * 0.2);
          const flicked = Math.abs(dx) > 24 && Math.abs(velocity) > FLICK_PX_PER_MS;
          if (Math.abs(dx) > threshold || flicked) turn(dx < 0 ? 1 : -1, "swipe");
        }
      } else if (!wasAbandoned && !wasInert) {
        const moved = Math.hypot(dx, e.clientY - startY);
        if (moved <= TAP_SLOP_PX && dt <= TAP_MAX_MS && !hasTextSelection()) {
          tapZone(e.clientX);
        }
      }

      pointerId = null;
      isDragging = false;
      inert = false;
      abandoned = false;
    };

    const tapZone = (clientX: number) => {
      const rect = frame.getBoundingClientRect();
      const f = (clientX - rect.left) / Math.max(1, rect.width);
      // Thirty per cent each side. Wider than the classic 25% because the thumb of a hand holding
      // a phone lands about a fifth of the way in, and the centre zone is still 40% — plenty for
      // a deliberate tap on the text.
      if (f < 0.3) turn(-1, "tap");
      else if (f > 0.7) turn(1, "tap");
      else cb.current.onToggleChrome();
    };

    const onLostCapture = () => {
      if (isDragging) {
        cb.current.paginator?.clearDragOffset();
        cb.current.paginator?.setDragging(false);
      }
      // A cancelled gesture may leave the transform exactly where it started, in which case no
      // `transitionend` ever fires and the promoted layer would sit there until the safety timer.
      // Demote it now: a cancelled drag has nothing left to animate.
      cb.current.paginator?.clearWillChange();
      reset();
    };

    /**
     * ⚠️ **Native text drag-and-drop steals the swipe, and this is not a hypothetical.**
     *
     * Once any text selection exists in the reader — and one exists the moment a finger or a mouse
     * drags across a paragraph — a subsequent drag that starts on selected text makes the browser
     * begin a *drag operation* instead. It fires `dragstart`, then sends us `pointercancel`, and
     * our gesture dies mid-way: the page follows the finger for 20 px and then snaps back. The user
     * reads this as "swiping stopped working", intermittently, and only after they have selected
     * something — which is exactly the kind of bug nobody can reproduce on request.
     *
     * Reproduced in Chrome: select a phrase by dragging, then swipe from that phrase. Confirmed
     * fixed by this one line.
     *
     * Blanket, including images: this is a reading surface and the swipe is its primary gesture.
     * Dragging a picture out of a book to another app is not worth a page turn that sometimes
     * doesn't happen. Copy, select and the context menu are all untouched.
     */
    const onDragStart = (e: DragEvent) => e.preventDefault();

    frame.addEventListener("pointerdown", onPointerDown);
    frame.addEventListener("pointermove", onPointerMove);
    frame.addEventListener("pointerup", onPointerUp);
    frame.addEventListener("pointercancel", onLostCapture);
    frame.addEventListener("lostpointercapture", onLostCapture);
    frame.addEventListener("dragstart", onDragStart);

    return () => {
      frame.removeEventListener("pointerdown", onPointerDown);
      frame.removeEventListener("pointermove", onPointerMove);
      frame.removeEventListener("pointerup", onPointerUp);
      frame.removeEventListener("pointercancel", onLostCapture);
      frame.removeEventListener("lostpointercapture", onLostCapture);
      frame.removeEventListener("dragstart", onDragStart);
      reset();
    };
  }, [frame, enabled, turn]);

  /* ── Keyboard ─────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!enabled) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;

      const p = cb.current.paginator;
      switch (e.key) {
        case "ArrowRight":
        case "PageDown":
          e.preventDefault();
          turn(1, "key");
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          turn(-1, "key");
          break;
        case " ": {
          // ⚠️ **P1's named conflict, decided.** "A media player uses Space for play/pause, and this
          // app is both. When narration lands, pick one deliberately — the likely answer is Space =
          // play/pause and Shift+Space = back, with turning left to the arrows and the tap zones."
          //
          // That is what this is, with one condition P1 could not have written: the pick applies
          // only when there IS something to play. A person reading an un-narrated book on a laptop
          // presses Space to turn the page, and a Space bar that silently does nothing on some books
          // and turns the page on others is worse than either choice made consistently. The
          // *presence of a narration* is the discriminator, and it is visible on screen — the
          // playback bar is either there or it is not.
          const play = cb.current.onTogglePlay;
          e.preventDefault();
          if (play && !e.shiftKey) play();
          else turn(e.shiftKey ? -1 : 1, "key");
          break;
        }
        case "Home":
          if (!p) break;
          e.preventDefault();
          goTo(0, "key");
          break;
        case "End":
          if (!p) break;
          e.preventDefault();
          goTo(p.maxColumn(), "key");
          break;
        default:
          break;
      }
    };

    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [enabled, turn, goTo]);

  /* ── The pill's two derived values ────────────────────────────────────────
     Read off the Paginator rather than mirrored into state, exactly as Reader.tsx reads `turnNow`
     and `turnsTotal`: `column` is already state and its updates are what re-render, so these reads
     are always fresh, and keeping the turn arithmetic in one place means the pill cannot disagree
     with the footer about what page 14 is. */
  const narrationTurnIndex = turnOf(paginator, narrationColumn);
  const hereTurnIndex = paginator ? paginator.turnIndex() : null;

  return {
    following,
    /**
     * ⚠️ Not merely `!following`. §5.5 auto-refollows "when the user lands back on the narration's
     * page, or **after 20 s idle with that page displayed**" — so there is a twenty-second window,
     * every time a reader flips back and then flips forward again, in which `following` is false and
     * the reader is looking at exactly the page the pill offers to take them to. A pill that says
     * "Karti is on page 14 · Back to the narration" while page 14 is on screen is a control with no
     * effect, and tapping it does nothing visible, which teaches the reader that the pill is broken
     * on the one occasion they will next need it.
     */
    showFollowPill:
      !following && narrationTurnIndex !== null && narrationTurnIndex !== hereTurnIndex,
    narrationTurn: narrationTurnIndex === null ? null : narrationTurnIndex + 1,
    turn,
    goTo,
    stopFollowing,
    setFollowing,
    backToNarration,
  };
}

/**
 * Column index → page-turn index. In spread mode one turn shows two columns.
 *
 * `Paginator.snap()` aligns to the turn boundary and `colsPerTurn` divides it out; going through
 * both rather than reimplementing the arithmetic is what keeps the pill, the footer and the
 * transform agreeing about what "page 14" means when the window is 1100 px wide.
 */
function turnOf(p: Paginator | null, column: number | null | undefined): number | null {
  if (!p || column == null) return null;
  const per = p.geometry?.colsPerTurn ?? 1;
  return Math.floor(p.snap(column) / Math.max(1, per));
}

/**
 * Rubber-band the drag at the ends of a part.
 *
 * Without it, dragging past the last page shows an empty frame and the reader cannot tell whether
 * the book ended or the app broke. With it, the page moves a third as far and springs back, which
 * every touch platform has trained people to read as "there is nothing here".
 */
function resist(dx: number, p: Paginator): number {
  const past = (dx > 0 && p.atStart()) || (dx < 0 && p.atEnd());
  return past ? dx * 0.32 : dx;
}

function hasTextSelection(): boolean {
  const sel = getSelection();
  return Boolean(sel && !sel.isCollapsed && sel.toString().trim().length > 0);
}
